import React, { useCallback, useMemo, useState } from 'react'
import { Grid, MenuItem, Typography, IconButton } from '@material-ui/core'
import { Loader } from '@refera/ui-web'

import {
  User,
  DocumentText,
  ShieldTick,
  Logout1,
  ArrowRight3,
  Message,
  House2,
  People,
  UserOctagon,
  SecuritySafe,
  RowHorizontal,
  Element4 as WidgetsIcon,
} from '@refera/ui-icons'

import CircleIcon from '@mui/icons-material/Circle'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import useStyles from './styles'
import { ROUTES } from '_/utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import { userSelector } from '_/modules/authentication/selectors'
import { logout, setUserProfile, postAccessHistory } from '_modules/authentication/actions'
import { navigate } from '@reach/router'
import { menuItemsSelector } from '_/modules/menu-items/selector'
import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'
import { getMenuItems } from '_/modules/menu-items/actions'
import { getIntermediaryProfileMenuItem } from './constants'
import { USER_ROLES } from '_/utils/user'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

export const HEADER_THEME = {
  DEFAULT: 'default',
  NO_BUTTONS: 'noButtons',
}

const HeaderItems = ({ handleNavigation, handleClose }) => {
  const styles = useStyles()
  const user = useSelector(userSelector)
  const dispatch = useDispatch()
  const hasProfiles = user?.profiles.length > 0
  const profiles = user?.profiles
  const userMenuItems = useSelector(menuItemsSelector)
  const lgpdLinks = user?.lgpdLinks
  const [isLoading, setIsLoading] = useState(false)

  const {
    checkPathPermission,
    isIntermediary,
    isSAAS,
    isAdmin,
    checkIfUserDoesNotPermission,
    checkUserPermission,
  } = useRolePermission()
  const { showToast } = useToast()

  const getMenuSecondary = useMemo(() => {
    const items = userMenuItems?.filter(item => item.type === 'secondary') ?? []

    if (isIntermediary || isSAAS) return [...items, getIntermediaryProfileMenuItem(user?.agency)]
    return items
  }, [userMenuItems, user, user?.agency])

  const handleUserTerms = useCallback(
    item => {
      const link = item === 'term' ? lgpdLinks[0] : lgpdLinks[1]
      window.open(link, '_blank')
      handleClose()
    },
    [lgpdLinks]
  )

  const handleClick = useCallback(
    path => {
      let newPath = path

      if (path.includes(ROUTES.AGENCY)) {
        if (
          checkIfUserDoesNotPermission(PERMISSIONS.INTERMEDIARY_REGISTRY, PERMISSIONS_ACTIONS.READ)
        ) {
          return null
        }
      } else {
        const userCanAccessPath = checkPathPermission(path)
        if (!userCanAccessPath) {
          return showToast({
            type: 'error',
            message: 'Você não tem permissão para esta ação.',
          })
        }
      }

      handleClose()
      if (!path.startsWith('/')) {
        newPath = `/${path}`
      }
      return navigate(newPath)
    },
    [checkPathPermission, showToast]
  )

  const handleNavigate = useCallback(
    path => {
      handleClose()
      navigate(path)
    },
    [handleClose]
  )

  const handleLogout = useCallback(() => {
    dispatch(logout())
    navigate('/login')
  }, [dispatch])

  const handleSelectProfile = useCallback(
    async name => {
      setIsLoading(true)
      await dispatch(setUserProfile(user?.authToken, { profile: name })).then(async () => {
        await new Promise(resolve => setTimeout(resolve, 2000))

        dispatch(
          getMenuItems({
            active: true,
          })
        )
        await dispatch(postAccessHistory()).then(() => {
          setIsLoading(false)
          handleNavigation(name === USER_ROLES.TRADESMAN ? ROUTES.TRADESMAN : ROUTES.WIDGETS)
        })
      })

      return null
    },
    [dispatch, user]
  )

  const changeProfile = profile => {
    if (profile !== user?.activeProfile?.name) {
      handleSelectProfile(profile)
    }
  }

  const renderMenuProfile = useMemo(() => {
    return (
      <>
        <MenuItem className={styles.customMenuItem}>
          <Grid className={styles.userCircle}>
            <User className={styles.userIcon} />
          </Grid>
          <Typography className={styles.userLabel}>{user?.name}</Typography>
        </MenuItem>

        <Grid className={styles.profileMenu}>
          {hasProfiles &&
            profiles.map(profileName => {
              if (profileName === USER_ROLES.REFERA) {
                return (
                  <MenuItem
                    className={styles.profileItem}
                    onClick={() => changeProfile(profileName)}
                  >
                    <IconButton disabled>
                      {isAdmin ? (
                        <CheckCircleIcon className={[styles.profileIcon, styles.active]} />
                      ) : (
                        <CircleIcon className={styles.profileIcon} />
                      )}
                    </IconButton>
                    <Typography className={styles.menuItem}>Acesso Refera</Typography>
                  </MenuItem>
                )
              }

              if (profileName === USER_ROLES.INTERMEDIARY || profileName === USER_ROLES.SAAS) {
                return (
                  <MenuItem
                    className={styles.profileItem}
                    onClick={() => changeProfile(profileName)}
                  >
                    <IconButton disabled>
                      {isSAAS || isIntermediary ? (
                        <CheckCircleIcon className={[styles.profileIcon, styles.active]} />
                      ) : (
                        <CircleIcon className={styles.profileIcon} />
                      )}
                    </IconButton>
                    <Typography className={styles.menuItem}>Acesso Intermediária</Typography>
                  </MenuItem>
                )
              }

              if (profileName === USER_ROLES.TRADESMAN) {
                return (
                  <MenuItem
                    className={styles.profileItem}
                    onClick={() => changeProfile(profileName)}
                  >
                    <IconButton disabled>
                      {user?.activeProfile?.name === USER_ROLES.TRADESMAN ? (
                        <CheckCircleIcon className={[styles.profileIcon, styles.active]} />
                      ) : (
                        <CircleIcon className={styles.profileIcon} />
                      )}
                    </IconButton>
                    <Typography className={styles.menuItem}>Acesso Prestador</Typography>
                  </MenuItem>
                )
              }

              if (profileName === USER_ROLES.LESSEE) {
                return (
                  <MenuItem
                    className={styles.profileItem}
                    onClick={() => changeProfile(profileName)}
                  >
                    <IconButton disabled>
                      {user?.activeProfile?.name === USER_ROLES.LESSEE ? (
                        <CheckCircleIcon className={[styles.profileIcon, styles.active]} />
                      ) : (
                        <CircleIcon className={styles.profileIcon} />
                      )}
                    </IconButton>
                    <Typography className={styles.menuItem}>Acesso Inquilino</Typography>
                  </MenuItem>
                )
              }

              return null
            })}
        </Grid>
      </>
    )
  }, [user, isAdmin, profiles, hasProfiles])

  const menuItemsMock = useMemo(() => {
    const showWidgetsButton = !checkIfUserDoesNotPermission(
      PERMISSIONS.WIDGETS,
      PERMISSIONS_ACTIONS.READ,
      undefined,
      false
    )

    return (
      <>
        <button
          key="side-menu-terms_user"
          onClick={() =>
            checkUserPermission({
              permission: PERMISSIONS.IA_ASSISTANCE,
              permissionAction: [PERMISSIONS_ACTIONS.READ],
              functionAccess: () => handleNavigate('/assistentes-ia'),
            })
          }
          type="button"
          className={styles.menuItems}
        >
          <Message className={styles.menuIcon} />
          <Grid className={styles.menuItem}>
            <Typography className={styles.menuItem}>IA - Assistentes</Typography>
          </Grid>
          <ArrowRight3 className={styles.arrow} />
        </button>
        <button
          key="side-menu-terms_user"
          onClick={() =>
            checkUserPermission({
              permission: PERMISSIONS.AI_TRAINING,
              permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
              functionAccess: () => handleNavigate(ROUTES.AI_TRAINING),
            })
          }
          type="button"
          className={styles.menuItems}
        >
          <Message className={styles.menuIcon} />
          <Grid className={styles.menuItem}>
            <Typography className={styles.menuItem}>Treinamento de IA</Typography>
          </Grid>
          <ArrowRight3 className={styles.arrow} />
        </button>

        {showWidgetsButton && (
          <button
            type="button"
            key="side-menu-widgets"
            onClick={() => handleNavigate(ROUTES.MANAGE_WIDGETS)}
            className={styles.menuItems}
          >
            <WidgetsIcon className={styles.menuIcon} />
            <Grid className={styles.menuItem}>
              <Typography className={styles.menuItem}>Widgets</Typography>
            </Grid>
            <ArrowRight3 className={styles.arrow} />
          </button>
        )}

        <button
          key="side-menu-terms_user"
          onClick={() => handleUserTerms('term')}
          type="button"
          className={styles.menuItems}
        >
          <DocumentText className={styles.menuIcon} />
          <Grid className={styles.menuItem}>
            <Typography className={styles.menuItem}>Termos de uso</Typography>
          </Grid>
          <ArrowRight3 className={styles.arrow} />
        </button>
        <button
          key="side-menu-terms_user"
          onClick={() => handleUserTerms('policy')}
          type="button"
          className={styles.menuItems}
        >
          <ShieldTick className={styles.menuIcon} />
          <Grid className={styles.menuItem}>
            <Typography className={styles.menuItem}>Política de privacidade</Typography>
          </Grid>
          <ArrowRight3 className={styles.arrow} />
        </button>
        <button
          key="side-menu-terms_user"
          onClick={handleLogout}
          type="button"
          className={styles.menuItems}
        >
          <Logout1 className={styles.logoutIcon} />
          <Grid className={styles.menuItem}>
            <Typography className={styles.logoutLabel}>Sair</Typography>
          </Grid>
        </button>
      </>
    )
  }, [user?.activeProfile?.name, handleNavigate])

  const renderIconImage = {
    [ROUTES.BUDGET_TEMPLATE]: <ShieldTick className={styles.menuIcon} />,
    [`/${ROUTES.AGENCY}/${user.agency}`]: <House2 className={styles.menuIcon} />,
    [ROUTES.REFERA_USERS]: <UserOctagon className={styles.menuIcon} />,
    [ROUTES.GROUP_PERMISSIONS]: <SecuritySafe className={styles.menuIcon} />,
    [ROUTES.PROFILES]: <People className={styles.menuIcon} />,
    [ROUTES.CATEGORY]: <Message className={styles.menuIcon} />,
    [ROUTES.PROBLEMS]: <Message className={styles.menuIcon} />,
    [ROUTES.SERVICE]: <Message className={styles.menuIcon} />,
    [ROUTES.STEP_MESSAGE]: <Message className={styles.menuIcon} />,
    [ROUTES.STEP_MESSAGE]: <Message className={styles.menuIcon} />,
    [ROUTES.AREAS_OF_ACTIVITY]: <RowHorizontal className={styles.menuUserIcon} />,
  }

  return (
    <>
      {isLoading && <Loader hasBackdrop open={isLoading} label="Aguarde..." />}
      {renderMenuProfile}
      {getMenuSecondary.map(item => {
        const itemPath = !item?.path.startsWith('/') ? `/${item?.path}` : item?.path
        return (
          <button
            key={`side-menu-${item.id}`}
            onClick={() => handleClick(item.path)}
            type="button"
            className={styles.menuItems}
          >
            {renderIconImage?.[itemPath] ?? <User className={styles.menuUserIcon} />}
            <Grid className={styles.menuItem}>
              <Typography className={styles.menuItem}>{item.label}</Typography>
            </Grid>
            <ArrowRight3 className={styles.arrow} />
          </button>
        )
      })}
      {user?.activeProfile === USER_ROLES.REFERA && (
        <button
          key="side-menu-terms_user"
          onClick={() => handleNavigate(ROUTES.COVERAGE_RULES)}
          type="button"
          className={styles.menuItems}
        >
          <Message className={styles.menuIcon} />
          <Grid className={styles.menuItem}>
            <Typography className={styles.menuItem}>Regra de cobertura</Typography>
          </Grid>
          <ArrowRight3 className={styles.arrow} />
        </button>
      )}
      {menuItemsMock}
    </>
  )
}

HeaderItems.defaultProps = {
  className: '',
  theme: HEADER_THEME.DEFAULT,
}

export default React.memo(HeaderItems)
